import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface Volunteer {
  id: number;
  parent_name: string;
  role: string;
  checked_in_at: string | null;
}

interface Parent {
  id: number;
  name: string;
  parent_type: string;
}

interface VolunteersModalProps {
  eventId: number;
  title: string;
  onClose: () => void;
}

const VolunteersModal: React.FC<VolunteersModalProps> = ({ eventId, title, onClose }) => {
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [allParents, setAllParents] = useState<Parent[]>([]);
  const [selectedParent, setSelectedParent] = useState<number | ''>('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchVolunteers = async () => {
    try {
      const response = await api.get(`events/${eventId}/volunteers/`);
      setVolunteers(response.data);
    } catch (error) {
      console.error('Failed to fetch volunteers', error);
    }
  };

  useEffect(() => {
    const fetchParents = async () => {
      try {
        const response = await api.get(`parents/`);
        setAllParents(response.data);
        await fetchVolunteers();
      } catch (error) {
        console.error('Failed to fetch parents', error);
      } finally {
        setLoading(false);
      }
    };
    fetchParents();
  }, [eventId]);

  const handleAddVolunteer = async () => {
    if (!selectedParent || !role) return;
    try {
      await api.post(`events/${eventId}/volunteer/`, 
        { parent_id: selectedParent, role }
      );
      await fetchVolunteers();
      setSelectedParent('');
      setRole('');
    } catch (error) {
      console.error('Failed to add volunteer', error);
      alert('Erro ao registrar trabalho.');
    }
  };

  const getParentTypeBadge = (type: string) => {
    switch (type) {
      case 'Conecta': return <span className="badge badge-success">Pai Conecta</span>;
      case 'Rendimento': return <span className="badge badge-info">Rendimento</span>;
      default: return <span className="badge badge-secondary">Geral</span>;
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
    }}>
      <div className="card" style={{ width: '700px', maxWidth: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Registro de Trabalho (Voluntários)</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
        </div>
        
        <p style={{ marginBottom: '20px' }}>Evento: <strong>{title}</strong></p>

        <div style={{ marginBottom: '20px', backgroundColor: 'var(--bg-secondary)', padding: '15px', borderRadius: '8px' }}>
          <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Registrar quem trabalhou:</p>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
            <div style={{ flex: 2 }}>
              <label style={{ fontSize: '0.8rem' }}>Pai/Mãe</label>
              <select 
                value={selectedParent} 
                onChange={(e) => setSelectedParent(Number(e.target.value))}
                style={{ width: '100%' }}
              >
                <option value="">Selecionar...</option>
                {allParents
                  .map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.parent_type})</option>
                  ))
                }
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '0.8rem' }}>Papel/O que fez</label>
              <input 
                type="text" 
                value={role} 
                onChange={(e) => setRole(e.target.value)}
                placeholder="Ex: Recepção"
                style={{ width: '100%' }}
              />
            </div>
            <button className="btn" onClick={handleAddVolunteer} disabled={!selectedParent || !role}>
              Registrar
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
          <table style={{ width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th>Nome</th>
                <th>Papel</th>
                <th>Horário Registro</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: '20px' }}>Carregando...</td></tr>
              ) : volunteers.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-light)' }}>
                    Nenhum registro de trabalho encontrado.
                  </td>
                </tr>
              ) : (
                volunteers.map(vol => (
                  <tr key={vol.id}>
                    <td>{vol.parent_name}</td>
                    <td><span className="badge badge-info">{vol.role}</span></td>
                    <td>{vol.checked_in_at ? new Date(vol.checked_in_at).toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div style={{ marginTop: '20px', textAlign: 'right' }}>
          <span style={{ marginRight: '10px', fontWeight: 'bold' }}>Total: {volunteers.length}</span>
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default VolunteersModal;
